import { Component, inject, Injector, Input, OnDestroy, OnInit, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { DisplayTimeZoneService } from '@modules/common/services/display-timezone.service';
import { Tab } from '@modules/common/types/tab';
import { ModalService } from '@modules/modal/services/modal.service';
import { SheetFolderListStateService } from '@modules/sheets/services/sheet-folder-list-state.service';
import { SheetFoldersService } from '@modules/sheets/services/sheet-folders.service';
import { SheetsListStateService } from '@modules/sheets/services/sheets-list-state.service';
import { SheetsService } from '@modules/sheets/services/sheets.service';
import { SheetsAppStateService } from '@modules/sheets/services/state.service';
import { ListState } from '@modules/sheets/types/list-state';
import { Sheet } from '@modules/sheets/types/sheet';
import { SheetFolder } from '@modules/sheets/types/sheet-folder';
import { SheetFoldersFilters } from '@modules/sheets/types/sheet-folders-filters';
import { SheetsFilters } from '@modules/sheets/types/sheets-filters';
import { VirtualFolder } from '@modules/sheets/types/virtual-folder';
import { openWorkbookInEditor } from '@modules/sheets/open-workbook';
import { AdvancedSearchService } from '@modules/search/services/advanced-search.service';
import { StateKey } from '@modules/settings/types/state-key';
import { SplitViewService } from '@modules/split-view/services/split-view.service';
import { combineLatest, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { Application } from '@modules/common/types/application';

@Component({
  selector: 'app-sheets-list-container',
  templateUrl: './sheets-list-container.component.html',
  styleUrls: ['./sheets-list-container.component.less'],
  standalone: false,
  providers: [
    ...SheetsListStateService.providers({
      stateKey: StateKey.sheetsListState,
      withAdvancedSearch: true,
    }),
    ...SheetFolderListStateService.providers({
      stateKey: StateKey.sheetFoldersListState,
      withAdvancedSearch: true,
    }),
  ],
})
export class SheetsListContainerComponent implements OnInit, OnDestroy {
  /** Folded to its edge tab: the list's saved state, or, beside an open workbook, the page's (SBUX-1145). */
  @Input() public minimized: boolean;
  /**
   * SBUX-1145 (Wook, 2026-10-27: "Yes, add a list toggle"): the list beside an open workbook, where a click opens the
   * clicked workbook in the editor. The editor then names it in the app state, so a refused switch changes nothing.
   */
  @Input() public inEditor = false;
  public searchQuery: string;
  public listState: ListState;
  public sidebarFilter: VirtualFolder;
  public quickSheetFolder: SheetFolder;
  public quickSheet: Sheet;
  public loading: boolean;
  public tabs: Tab[] = [
    { title: 'Folders', value: 'sheetFolders' },
    // AM-36: the item is a workbook. Every sibling names the item in this tab -- Files says
    // 'Files', Notes says 'Notes'. The clone's mechanical Note -> Sheet rename left this one
    // naming a sheet, which is a tab INSIDE a workbook.
    { title: 'Workbooks', value: 'sheets' },
  ];
  public selectedTab: Exclude<ListState, 'tabs'> = 'sheets';
  public sheetFoldersFilters: SheetFoldersFilters;
  public sheetsFilters: SheetsFilters;
  public debug: 'score' = null;
  public containerName: string | null = null;

  private readonly sheetsListStateService = inject(SheetsListStateService, { self: true });
  private readonly sheetFolderListStateService = inject(SheetFolderListStateService, { self: true });
  private readonly advancedSearchService = inject(AdvancedSearchService);
  private readonly sheetsService = inject(SheetsService);
  private readonly sheetFoldersService = inject(SheetFoldersService);
  private readonly displayTimeZoneService = inject(DisplayTimeZoneService);
  private readonly router = inject(Router);

  /**
   * The account zone as a SOURCE, not a one-off read (SBUX-829). `DisplayTimeZoneService` starts on the
   * BROWSER zone and is corrected to the account's once space settings load, so a single early read
   * captures the wrong zone forever. Streaming it rebuilds the filters when the real zone arrives.
   */
  private readonly displayTimeZone$ = this.displayTimeZoneService.resolvedTimeZone$;

  private alive = new Subject<void>();

  protected readonly isAdvancedSearchApplied: Signal<boolean> = toSignal(
    this.advancedSearchService.isAdvancedSearchApplied(Application.sheets),
    { initialValue: false },
  );

  constructor(
    private splitViewService: SplitViewService,
    private stateService: SheetsAppStateService,
    private modalService: ModalService,
    private searchService: AdvancedSearchService,
    private injector: Injector,
  ) {
    combineLatest([
      this.sheetFolderListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        // `displayTimeZone` FIRST: applyListState/applyVirtualFolder read it to reckon their day
        // windows in the account zone (SBUX-829).
        this.sheetFoldersFilters = new SheetFoldersFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });

    combineLatest([
      this.sheetsListStateService.value(),
      this.searchService.getState(),
      this.stateService.getVirtualFolder(),
      this.displayTimeZone$,
      this.stateService.getVirtualFolderReselections(),
    ])
      .pipe(takeUntil(this.alive))
      .subscribe(([list, search, folder, displayTimeZone]) => {
        this.sheetsFilters = new SheetsFilters({ displayTimeZone })
          .applyListState(list)
          .applyAdvancedFilters(search)
          .applyVirtualFolder(folder);
      });
  }

  public ngOnInit() {
    if (!this.inEditor) {
      this.splitViewService
        .getMinimized('sheetsList')
        .pipe(takeUntil(this.alive))
        .subscribe((minimized: boolean) => {
          this.minimized = minimized;
        });
    }

    this.stateService
      .getTabs()
      .pipe(takeUntil(this.alive))
      .subscribe((state: ListState) => {
        this.listState = state;
        if (this.listState !== 'tabs') {
          this.selectedTab = this.listState;
        }
      });

    combineLatest([this.searchService.getState(), this.stateService.getVirtualFolder()])
      .pipe(takeUntil(this.alive))
      .subscribe(([state, folder]) => {
        this.sidebarFilter = folder;
        this.containerName = state[Application.sheets]?.containerName ?? null;
        this.quickSheet = Sheet.fromAdvancedState(state, folder);
        this.quickSheetFolder = SheetFolder.fromAdvancedState(state, folder);
      });
  }

  ngOnDestroy() {
    this.alive.next();
    this.alive.complete();
  }

  /**
   * Actions
   */

  handleNewSheet(sheet = new Sheet()) {
    this.stateService.setMainView(sheet);
  }

  handleNewSheetFolder(sheetFolder = new SheetFolder()) {
    this.stateService.setMainView(sheetFolder);
  }

  handleDoubleClickSheet(sheet: Sheet) {
    if (this.inEditor) {
      this.switchEditorTo(sheet);
      return;
    }
    this.modalService.openFullForm(sheet, this.injector);
  }

  handleClickSheet(sheet: Sheet) {
    if (this.inEditor) {
      this.switchEditorTo(sheet);
      return;
    }
    this.stateService.setMainView(sheet);
  }

  /**
   * "Open" in a card's "…" menu opens the workbook in the editor, as the prototype's does (gap row 39). It
   * used to select the card, which a click already does. Selected first, as "Edit workbook" leaves it, so the
   * expanded header names it at once and closing the editor returns to it (AM-78's SM-LAYOUT).
   */
  handleOpenSheet(sheet: Sheet) {
    if (this.inEditor) {
      this.switchEditorTo(sheet);
      return;
    }
    this.stateService.setMainView(sheet);
    void openWorkbookInEditor(this.router, sheet.id);
  }

  /** Beside an open workbook (SBUX-1145): the editor switches to `sheet` unless its own guard refuses. */
  private switchEditorTo(sheet: Sheet): void {
    void openWorkbookInEditor(this.router, sheet.id);
  }

  handleDoubleClickSheetFolder(sheetFolder: SheetFolder) {
    this.modalService.openFullForm(sheetFolder, this.injector);
  }

  handleClickSheetFolder(sheetFolder: SheetFolder) {
    this.stateService.setMainView(sheetFolder);
  }

  doDebug() {
    switch (this.debug) {
      case 'score':
        this.debug = null;
        break;
      default:
        this.debug = 'score';
    }
  }
}
